import type { AssociateData } from '../services/otpAuthService';

/**
 * Sezioni dell'area riservata che il titolare può abilitare ai suoi dipendenti
 * (spec dipendenti dello studio §6.1). Sono anche i codici che il backend controlla
 * con `richiedeSezione` (403 `SEZIONE_NON_ABILITATA`).
 */
export type CodiceSezione =
  | 'takata'
  | 'chat'
  | 'circolari'
  | 'svincolo'
  | 'voucher'
  | 'formalita'
  | 'supporto'
  | 'eventi'
  | 'sondaggi'
  | 'collabora'
  | 'facile'
  | 'recensioni';

export const CODICI_SEZIONE: CodiceSezione[] = [
  'takata', 'chat', 'circolari', 'svincolo', 'voucher', 'formalita',
  'supporto', 'eventi', 'sondaggi', 'collabora', 'facile', 'recensioni',
];

/** Nomi delle sezioni nel modulo del titolare (spec §6.1). */
export const ETICHETTE_SEZIONE: Record<CodiceSezione, string> = {
  takata: 'Verifica Takata',
  chat: 'Chat',
  circolari: 'Circolari',
  svincolo: 'Svincolo atti',
  voucher: 'Rimborso voucher',
  formalita: 'Formalità e canali',
  supporto: 'Supporto (ticket)',
  eventi: 'Eventi e Diretta Live',
  sondaggi: 'Sondaggi',
  collabora: 'Collaborazioni',
  facile: 'Facile.it',
  recensioni: 'Recensioni',
};

/** Sezioni spuntate per un nuovo dipendente (spec §6.1). */
export const SEZIONI_PREDEFINITE: CodiceSezione[] = ['takata', 'chat', 'circolari'];

/**
 * La chat dei dipendenti (Piano 2, chat per persona) è pronta: la sezione `chat` vale come le altre.
 * Con `false` la chat resterebbe nascosta al dipendente anche se abilitata dal titolare.
 */
export const CHAT_DIPENDENTI_PRONTA = true;

/**
 * Il dato dell'associato è di un dipendente? Senza dati o senza `ruolo` (backend precedenti)
 * è il titolare. Con `dipendente` presente e `ruolo` assente si sceglie il caso più chiuso.
 */
export function eDipendente(a?: AssociateData | null): boolean {
  if (!a) return false;
  if (a.ruolo === 'dipendente') return true;
  return a.ruolo !== 'titolare' && !!a.dipendente;
}

/**
 * La sezione si mostra? Per il titolare sempre (restano le condizioni di oggi di ogni card);
 * per il dipendente solo se il titolare gliel'ha abilitata (la chat solo con `CHAT_DIPENDENTI_PRONTA`).
 */
export function sezioneVisibile(a: AssociateData | null | undefined, codice: CodiceSezione): boolean {
  if (!eDipendente(a)) return true;
  if (codice === 'chat' && !CHAT_DIPENDENTI_PRONTA) return false;
  const sezioni = a?.dipendente?.sezioni;
  return Array.isArray(sezioni) && sezioni.includes(codice);
}

/**
 * Sezione da cui dipende una categoria del centro notifiche (piano 3, N1; stessa mappa di
 * `backend/utils/notificheSezioni.js`). Le categorie assenti (sondaggio, tesseramento, community,
 * sistema) sono solo del titolare. `rete_certificata` arriva al dipendente solo per le collaborazioni.
 */
const SEZIONE_CATEGORIA_NOTIFICA: Record<string, CodiceSezione> = {
  svincolo_atti: 'svincolo',
  voucher: 'voucher',
  circolari: 'circolari',
  eventi: 'eventi',
  supporto: 'supporto',
  feedback: 'supporto',
  rete_certificata: 'collabora',
  community_chat: 'chat',
};

/**
 * La persona può avere notifiche di questa categoria? Il titolare tutte; il dipendente solo quelle
 * mappate su una sezione che ha (filtri della campanella).
 */
export function categoriaNotificaVisibile(a: AssociateData | null | undefined, categoria: string): boolean {
  if (!eDipendente(a)) return true;
  const sezione = Object.prototype.hasOwnProperty.call(SEZIONE_CATEGORIA_NOTIFICA, categoria)
    ? SEZIONE_CATEGORIA_NOTIFICA[categoria]
    : null;
  return !!sezione && sezioneVisibile(a, sezione);
}

/**
 * Il mio id persona nella chat (Piano 2): l'id del dipendente per il dipendente, l'id dello studio per il
 * titolare (identico a prima). È l'id che il server usa in menzioni, membri, eventi e blocchi.
 * Stringa vuota senza dati (o dipendente senza id: mai l'id dello studio al suo posto).
 */
export function idPersonaChat(a?: AssociateData | null): string {
  if (!a) return '';
  if (eDipendente(a)) {
    const id = a.dipendente?.id;
    return typeof id === 'string' ? id : '';
  }
  return typeof a.id === 'string' ? a.id : '';
}

/** «Mario Rossi» per il dipendente, stringa vuota per il titolare. */
export function nomeDipendente(a?: AssociateData | null): string {
  if (!eDipendente(a) || !a?.dipendente) return '';
  return [a.dipendente.nome, a.dipendente.cognome].filter(Boolean).join(' ').trim();
}

/** Payload (non verificato) di un JWT in base64url; null se manca o è illeggibile. Non lancia mai. */
function payloadToken(token?: string | null): Record<string, unknown> | null {
  try {
    const parte = token ? token.split('.')[1] : '';
    if (!parte) return null;
    const base64 = parte.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)));
    return payload && typeof payload === 'object' ? payload : null;
  } catch (e) {
    return null;
  }
}

/**
 * La sessione salvata in questo browser è di un dipendente? Legge `associate_data` e, se i dati
 * dicono «studio» (ponte app → sito prima di `/auth/me`), il tipo del token in `associate_token`.
 * Senza axios né otpAuthService: la usano gli hook dei socket e la gestione della fine sessione.
 * Non lancia mai.
 */
export function sessioneDiDipendente(): boolean {
  try {
    const dati = window.localStorage.getItem('associate_data');
    if (dati && eDipendente(JSON.parse(dati))) return true;
  } catch (e) {
    // dati illeggibili: decide il token
  }
  try {
    const payload = payloadToken(window.localStorage.getItem('associate_token'));
    return !!payload && payload.type === 'dipendente';
  } catch (e) {
    return false;
  }
}

/**
 * Sessione aperta con «Accedi come associato» (`impersonatedBy` nel payload di `associate_token`,
 * letto senza verifica: decide comunque il server con 403 `IMPERSONIFICAZIONE`). Non lancia mai.
 */
export function sessioneInImpersonificazione(): boolean {
  try {
    const payload = payloadToken(window.localStorage.getItem('associate_token'));
    return !!(payload && payload.impersonatedBy);
  } catch (e) {
    return false;
  }
}

/**
 * Due token associato sono della stessa persona? Stesso `associateId` e stesso tipo: titolare
 * (`type: 'associate'`) con titolare, oppure dipendente con lo stesso `dipendenteId`; anche
 * `impersonatedBy` deve coincidere («Accedi come associato» non è l'accesso dello studio).
 * Payload letti senza verifica: serve solo a non chiudere la sessione (e la push web del browser)
 * quando rientra lo stesso account. Nel dubbio false. Non lancia mai.
 */
export function stessaPersonaDelToken(precedente?: string | null, nuovo?: string | null): boolean {
  const a = payloadToken(precedente);
  const b = payloadToken(nuovo);
  if (!a || !b) return false;
  const testo = (v: unknown) => (v == null ? '' : String(v));
  if (!testo(a.associateId) || testo(a.associateId) !== testo(b.associateId)) return false;
  if (a.type !== b.type) return false;
  if (testo(a.impersonatedBy) !== testo(b.impersonatedBy)) return false;
  if (a.type === 'associate') return true;
  if (a.type === 'dipendente') return !!testo(a.dipendenteId) && testo(a.dipendenteId) === testo(b.dipendenteId);
  return false;
}

/**
 * Evento `storage` (arriva solo alle ALTRE schede dello stesso browser): in un'altra scheda la persona è cambiata
 * rispetto a quella con cui questa scheda lavora? `vecchio` è il valore che questa scheda stava usando.
 * - `associate_token`: questa scheda aveva una sessione e il nuovo token non è della stessa persona
 *   (`stessaPersonaDelToken`), compreso il token tolto all'uscita. Token rinnovato della stessa persona: no.
 * - `associate_data`: solo se i dati di una persona vengono tolti (gli aggiornamenti, es. `/auth/me` dopo il ponte
 *   dall'app, non sono un cambio: il cambio vero passa dal token).
 * Altre chiavi (e `null`, cioè `localStorage.clear()`, che decide chi ascolta): no. Non lancia mai.
 */
export function personaCambiataInAltraScheda(chiave: string | null, vecchio: string | null, nuovo: string | null): boolean {
  if (chiave === 'associate_token') {
    if (!vecchio || vecchio === nuovo) return false;
    return !stessaPersonaDelToken(vecchio, nuovo);
  }
  if (chiave === 'associate_data') {
    if (nuovo != null || !vecchio) return false;
    try {
      return !!idPersonaChat(JSON.parse(vecchio));
    } catch (e) {
      return false;
    }
  }
  return false;
}

/** `percorso` è `radice` o una sua sotto-pagina (non `/radice-altro`). */
const sotto = (percorso: string, radice: string) => percorso === radice || percorso.startsWith(`${radice}/`);

/**
 * Pagine che non usano la sessione dell'associato (`associate_token`): pannello admin (`/admin`, che la scrive per
 * «Accedi come associato» e la apre in un'altra scheda) e area associativa dei rappresentanti (`/area-associativa`,
 * token proprio). Lì un cambio di persona in un'altra scheda non va gestito: niente ricarica né lavoro perso.
 */
export function sessioneAssociatoNonInUso(percorso: string): boolean {
  return sotto(percorso, '/admin') || sotto(percorso, '/area-associativa');
}

/**
 * Evento di `window` (stessa scheda) con cui `useCambioPersonaAltraScheda` annuncia che in un'altra scheda la persona
 * è cambiata o è uscita. Lo ascoltano Header e menu mobile (`useGenerazionePersona`: rileggono la sessione e
 * rimontano la campanella, quindi il socket `/notifications`), la diretta e la chat (`ChatContext` chiude il socket
 * se il token non è più della stessa persona). Nessun dato nell'evento: ognuno rilegge il localStorage.
 */
export const EVENTO_PERSONA_CAMBIATA = 'unasca:persona-cambiata';

/**
 * Dove portare la scheda dopo un cambio di persona fatto in un'altra scheda: dall'area riservata all'accesso
 * associati; altrove null, cioè nessuna navigazione né ricarica (i moduli pubblici in compilazione, come il wizard
 * della Rete Certificata, non si perdono): campanella, socket e chat seguono `EVENTO_PERSONA_CAMBIATA`.
 */
export function destinazioneDopoCambioPersona(percorso: string): string | null {
  return sotto(percorso, '/area-riservata') ? '/accesso-associati' : null;
}

/**
 * Token con cui (ri)collegare il socket della chat, aperto col token `avvio`: quello attuale se è della stessa
 * persona (token rinnovato), null se è di un'altra persona o non c'è più (cambio o uscita in un'altra scheda:
 * niente riconnessione col token di un altro sullo stato della persona di prima). Se uno dei due token non si
 * legge decide il server, come prima: si usa quello attuale.
 */
export function tokenPerRiconnessione(avvio?: string | null, attuale?: string | null): string | null {
  if (!attuale) return null;
  if (attuale === avvio) return attuale;
  const leggibile = (t?: string | null) => {
    const p = payloadToken(t);
    return !!p && !!p.associateId && (p.type === 'associate' || p.type === 'dipendente');
  };
  if (!leggibile(avvio) || !leggibile(attuale)) return attuale;
  return stessaPersonaDelToken(avvio, attuale) ? attuale : null;
}
